(() => {
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* Overlay scroll lock. overflow:hidden on body zeros scrollY, which
     drops .is-scrolled and makes the homepage logo jump size/colour.
     Save the Y, freeze the header class, restore instantly on close. */
  const scrollLocks = Object.create(null);
  let lockedScrollY = 0;

  function overlayScrollLocked() {
    return (
      document.body.classList.contains("page-scroll-locked") ||
      document.body.classList.contains("cart-drawer-open") ||
      document.body.classList.contains("quick-view-open") ||
      document.body.classList.contains("search-modal-open") ||
      document.body.classList.contains("nav-open")
    );
  }

  /* The header is pinned once the announcement has scrolled away. On the
     homepage the absolute header reaches the top of the viewport at that
     exact scroll, so fixing it there moves nothing. Without a bar: 24px.
     Height is cached so the scroll handler never reads layout. */
  let pinLine = 0;
  function headerPastPin(y) {
    if (!pinLine) {
      const bar = document.querySelector(".announcement");
      pinLine = (bar && bar.offsetHeight) || 25;
    }
    return (y || 0) >= pinLine;
  }

  const phoneScrollLock = window.matchMedia("(max-width: 899px)");

  /* overflow:hidden does not stop a phone from scrolling the page under a
     menu or search. Pin the body, and put the scroll back on close. */
  function pinPhoneBody(y) {
    if (!phoneScrollLock.matches) return;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = "-" + (y || 0) + "px";
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  }

  function unpinPhoneBody() {
    const body = document.body;
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
  }

  /* Largest visual viewport seen during this lock. The keyboard shrinks
     it; closing the keyboard brings it back. Not the header's top. */
  let maxVisualHeight = 0;

  function noteViewportHeight() {
    const vv = window.visualViewport;
    const h = vv ? vv.height : window.innerHeight || 0;
    if (h > maxVisualHeight) maxVisualHeight = h;
  }

  noteViewportHeight();

  function lockPageScroll(key) {
    const name = key || "default";
    if (scrollLocks[name]) return;
    if (Object.keys(scrollLocks).length === 0) {
      maxVisualHeight = 0;
      noteViewportHeight();
      lockedScrollY = window.scrollY || 0;
      document.body.classList.add("page-scroll-locked");
      pinPhoneBody(lockedScrollY);
    }
    scrollLocks[name] = true;
  }

  /* Closing the phone keyboard snaps scrollY toward 0, often long after
     the body is unpinned. Hold the real position, then let the next
     finger take over. Never write the header's top from the viewport. */
  const PHONE_RESTORE_MS = 2800;
  const PHONE_DISARM_MS = 350;
  let restoreY = 0;
  let restoreUntil = 0;
  let restoreArmedAt = 0;
  let restoring = false;

  function applyHeaderPin(y) {
    const headerEl = document.querySelector("[data-header]");
    if (headerEl) headerEl.classList.toggle("is-scrolled", headerPastPin(y));
  }

  function writeScrollY(y) {
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, y);
    html.style.scrollBehavior = prevBehavior;
  }

  function clearLeftoverBodyPin() {
    if (overlayScrollLocked()) return;
    if (document.body.style.position === "fixed") unpinPhoneBody();
  }

  function phoneRestoreY() {
    if (!phoneScrollLock.matches || performance.now() >= restoreUntil) return null;
    return restoreY;
  }

  function armPhoneRestore(y) {
    restoreY = y;
    restoreArmedAt = performance.now();
    restoreUntil = restoreArmedAt + PHONE_RESTORE_MS;
  }

  function disarmPhoneRestore() {
    restoreUntil = 0;
  }

  /* A snap can land on the frames after the first scrollTo. Re-apply the
     pin twice, and put the page back if it jumped toward 0 again. */
  function reapplyPinFrames(count) {
    if (count <= 0) return;
    requestAnimationFrame(() => {
      if (phoneRestoreY() == null) return;
      clearLeftoverBodyPin();
      const now = window.scrollY || 0;
      if (restoreY > 40 && now < restoreY - 40 && !restoring) {
        restoring = true;
        writeScrollY(restoreY);
        restoring = false;
      }
      applyHeaderPin(restoreY);
      reapplyPinFrames(count - 1);
    });
  }

  function restorePhoneScroll(y) {
    if (restoring) return;
    restoring = true;
    clearLeftoverBodyPin();
    writeScrollY(y);
    applyHeaderPin(y);
    restoring = false;
    if (phoneScrollLock.matches && phoneRestoreY() != null) reapplyPinFrames(2);
  }

  function settlePhoneScroll() {
    if (!phoneScrollLock.matches || phoneRestoreY() == null || overlayScrollLocked()) return;
    clearLeftoverBodyPin();
    const y = window.scrollY || 0;
    if (restoreY > 40 && y < restoreY - 40) restorePhoneScroll(restoreY);
    else applyHeaderPin(Math.max(y, restoreY));
  }

  function keyboardFieldFocused() {
    const el = document.activeElement;
    return !!(
      el &&
      typeof el.matches === "function" &&
      el.matches("[data-search-input], [data-currency-filter]")
    );
  }

  /* The phone header never follows the keyboard. Matching
     visualViewport.offsetTop left the bar in the white band after the
     field blurred, and nudging scroll to clear that offset did not. */
  function clearPhoneKeyboardShift() {
    const root = document.documentElement;
    const body = document.body;
    if (body.classList.contains("phone-keyboard")) body.classList.remove("phone-keyboard");
    if (body.classList.contains("phone-vv-hold")) body.classList.remove("phone-vv-hold");
    if (root.style.getPropertyValue("--phone-keyboard-top")) {
      root.style.removeProperty("--phone-keyboard-top");
    }
  }

  /* While the keyboard field is focused, iOS can shove the pinned body.
     Put the pin back. Do not follow visualViewport.offsetTop. */
  function reassertPinnedBody() {
    if (!phoneScrollLock.matches || !overlayScrollLocked() || !keyboardFieldFocused()) return;
    pinPhoneBody(lockedScrollY);
    if ((window.scrollY || 0) === 0 || restoring) return;
    restoring = true;
    writeScrollY(0);
    restoring = false;
  }

  function placeSearch() {
    if (!document.body.classList.contains("search-modal-open")) return;
    if (!window.BelnaySearch || typeof window.BelnaySearch.place !== "function") return;
    window.BelnaySearch.place();
  }

  function onVisualViewport() {
    noteViewportHeight();
    clearPhoneKeyboardShift();
    placeSearch();
    if (phoneScrollLock.matches && overlayScrollLocked() && keyboardFieldFocused()) {
      reassertPinnedBody();
      return;
    }
    settlePhoneScroll();
  }

  function keyboardAway() {
    noteViewportHeight();
    const vv = window.visualViewport;
    const h = vv ? vv.height : window.innerHeight || 0;
    if (!maxVisualHeight) return true;
    return h >= maxVisualHeight - 40;
  }

  let pendingSettle = null;

  function unlockPageScrollWhenSettled(key) {
    const name = key || "default";
    if (!phoneScrollLock.matches || keyboardAway()) {
      if (pendingSettle) pendingSettle.cancel();
      unlockPageScroll(name);
      return;
    }
    if (pendingSettle && pendingSettle.key === name) return;
    if (pendingSettle) pendingSettle.cancel();

    const vv = window.visualViewport;
    let timer = 0;
    let settled = false;
    const cancel = () => {
      settled = true;
      if (vv) vv.removeEventListener("resize", onResize);
      window.clearTimeout(timer);
      if (pendingSettle && pendingSettle.key === name) pendingSettle = null;
    };
    const finish = () => {
      if (settled) return;
      cancel();
      unlockPageScroll(name);
    };
    const onResize = () => {
      noteViewportHeight();
      if (keyboardAway()) finish();
    };
    pendingSettle = { key: name, cancel };
    if (vv) vv.addEventListener("resize", onResize);
    timer = window.setTimeout(finish, 700);
  }

  function unlockPageScroll(key) {
    const name = key || "default";
    if (!scrollLocks[name]) return;
    delete scrollLocks[name];
    if (Object.keys(scrollLocks).length > 0) return;
    const y = lockedScrollY;
    unpinPhoneBody();
    document.body.classList.remove("page-scroll-locked");
    if (phoneScrollLock.matches) armPhoneRestore(y);
    restorePhoneScroll(y);
    clearPhoneKeyboardShift();
    if (phoneScrollLock.matches) requestAnimationFrame(settlePhoneScroll);
  }

  /* The tap that closed the overlay is already over before the guard
     arms. A later finger scroll must win; a synthetic scroll must not. */
  function onGestureDisarm() {
    if (!phoneScrollLock.matches) return;
    if (phoneRestoreY() == null) return;
    if (performance.now() - restoreArmedAt < PHONE_DISARM_MS) return;
    disarmPhoneRestore();
  }

  document.addEventListener("touchstart", onGestureDisarm, { capture: true, passive: true });
  document.addEventListener("pointerdown", onGestureDisarm, { capture: true, passive: true });

  /* A moving finger or a wheel is never the keyboard snap. Closing the
     size chart arms the hold 500ms after the tap, so a scroll that starts
     inside the disarm window was pulled back to the old spot on every
     scroll event for 2.8s. Any real scroll wins, whatever the clock says. */
  function onUserScrollDisarm() {
    if (phoneRestoreY() != null) disarmPhoneRestore();
  }
  document.addEventListener("touchmove", onUserScrollDisarm, { capture: true, passive: true });
  document.addEventListener("wheel", onUserScrollDisarm, { capture: true, passive: true });
  document.addEventListener("focusin", onVisualViewport);
  document.addEventListener("focusout", onVisualViewport);

  document.addEventListener(
    "touchmove",
    (event) => {
      if (!phoneScrollLock.matches) return;
      if (!document.body.classList.contains("page-scroll-locked")) return;
      const target = event.target;
      if (
        target &&
        typeof target.closest === "function" &&
        target.closest(
          ".currency-selector__list, .mobile-nav__root, .mobile-nav__shop-view, .mobile-nav__products, .search-modal__body, .cart-drawer__body, .quick-view__dialog, .size-chart__panel"
        )
      ) {
        return;
      }
      event.preventDefault();
    },
    { passive: false }
  );

  window.Belnay = window.Belnay || {};
  window.Belnay.lockPageScroll = lockPageScroll;
  window.Belnay.unlockPageScroll = unlockPageScroll;
  window.Belnay.unlockPageScrollWhenSettled = unlockPageScrollWhenSettled;
  window.Belnay.phoneScrollRestore = phoneRestoreY;
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", onVisualViewport, { passive: true });
    window.visualViewport.addEventListener("scroll", onVisualViewport, { passive: true });
  }

  // Smooth open/close for <details data-accordion>. Native details snaps
  // open and can scroll the row to the top of its scrollport — including
  // the Quick view info column. Animate only the panel so the summary
  // stays put and the copy grows downward. Skip when reduced motion is on.
  function nearestScrollPorts(el) {
    const ports = [];
    let node = el.parentElement;
    while (node && node !== document.documentElement) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
        ports.push(node);
      }
      node = node.parentElement;
    }
    return ports;
  }

  function bindAccordions(scope) {
    if (prefersReduced || typeof Element.prototype.animate !== "function") return;
    (scope || document).querySelectorAll("details[data-accordion]").forEach((details) => {
      if (details.dataset.accordionBound === "1") return;
      const summary = details.querySelector("summary");
      const panel = summary ? summary.nextElementSibling : null;
      if (!summary || !panel) return;
      details.dataset.accordionBound = "1";

      let animation = null;
      let isOpening = false;
      let isClosing = false;
      let unpinScroll = null;

      const releaseScroll = () => {
        if (typeof unpinScroll === "function") unpinScroll();
        unpinScroll = null;
      };

      const pinScrollPorts = (scrollers) => {
        if (!scrollers.length) return;
        const tops = scrollers.map((node) => node.scrollTop);
        const keep = () => {
          scrollers.forEach((node, index) => {
            node.scrollTop = tops[index];
          });
        };
        scrollers.forEach((node) => node.addEventListener("scroll", keep));
        unpinScroll = () => {
          scrollers.forEach((node) => node.removeEventListener("scroll", keep));
        };
        keep();
      };

      const panelHeight = () => panel.getBoundingClientRect().height;

      const animatePanel = (from, to, opening) => {
        if (animation) {
          if (typeof animation.commitStyles === "function") animation.commitStyles();
          animation.cancel();
        }
        isOpening = opening;
        isClosing = !opening;
        details.dataset.expanded = opening ? "true" : "false";
        panel.style.overflow = "hidden";
        panel.style.height = `${from}px`;
        animation = panel.animate(
          { height: [`${from}px`, `${to}px`] },
          { duration: 380, easing: "cubic-bezier(0.22, 1, 0.36, 1)", fill: "forwards" }
        );
        animation.onfinish = () => {
          if (typeof animation.commitStyles === "function") animation.commitStyles();
          animation.cancel();
          details.open = opening;
          animation = null;
          isOpening = false;
          isClosing = false;
          panel.style.height = opening ? "" : "0px";
          panel.style.overflow = opening ? "" : "hidden";
          releaseScroll();
        };
        animation.oncancel = () => {
          isOpening = false;
          isClosing = false;
        };
      };

      const expand = () => {
        const scrollers = nearestScrollPorts(details);
        const winY = window.scrollY || 0;

        if (scrollers.length) {
          details.open = true;
          pinScrollPorts(scrollers);
        } else {
          const html = document.documentElement;
          const prevOverflow = html.style.overflow;
          html.style.overflow = "hidden";
          details.open = true;
          html.style.overflow = prevOverflow;
          if ((window.scrollY || 0) !== winY) window.scrollTo(0, winY);
        }

        details.dataset.expanded = "true";
        const endHeight = panel.scrollHeight;
        panel.style.overflow = "hidden";
        panel.style.height = "0px";
        animatePanel(0, endHeight, true);
      };

      const collapse = () => {
        releaseScroll();
        const startHeight = panelHeight();
        details.dataset.expanded = "false";
        animatePanel(startHeight, 0, false);
      };

      summary.addEventListener("click", (event) => {
        event.preventDefault();
        if (isClosing || !details.open) expand();
        else if (isOpening || details.open) collapse();
      });
    });
  }

  window.Belnay.bindAccordions = bindAccordions;

  /* overflow:hidden drops position:sticky, so the buy column jumps to the
     top of the gallery and leaves the screen. Hold it where it was. */
  function pinProductInfo() {
    const info = document.querySelector(".main-product__info");
    if (!info || info.dataset.sizeChartPinned === "1") return;
    if (getComputedStyle(info).position !== "sticky") return;
    const rect = info.getBoundingClientRect();
    info.style.position = "fixed";
    info.style.top = rect.top + "px";
    info.style.left = rect.left + "px";
    info.style.width = rect.width + "px";
    info.style.zIndex = "1";
    info.dataset.sizeChartPinned = "1";
  }

  function releaseProductInfo() {
    const info = document.querySelector(".main-product__info");
    if (!info || info.dataset.sizeChartPinned !== "1") return;
    info.style.position = "";
    info.style.top = "";
    info.style.left = "";
    info.style.width = "";
    info.style.zIndex = "";
    delete info.dataset.sizeChartPinned;
  }

  const SIZE_CHART_MS = prefersReduced ? 0 : 500;

  function settleSizeChart(layer) {
    layer.classList.remove("is-open", "is-visible");
    layer.hidden = true;
    layer._sizeChartClosing = false;
    if (window.Belnay && typeof window.Belnay.unlockPageScroll === "function") {
      window.Belnay.unlockPageScroll("size-chart");
    }
    releaseProductInfo();
    const back = layer._sizeChartReturn;
    layer._sizeChartReturn = null;
    if (back && document.contains(back) && typeof back.focus === "function") {
      back.focus({ preventScroll: true });
    }
  }

  function closeSizeChart(layer) {
    if (!layer || layer.hidden || layer._sizeChartClosing) return;
    layer._sizeChartClosing = true;
    layer._sizeChartToken = (layer._sizeChartToken || 0) + 1;
    const token = layer._sizeChartToken;
    layer.classList.remove("is-visible");
    window.setTimeout(() => {
      if (layer._sizeChartToken !== token) return;
      settleSizeChart(layer);
    }, SIZE_CHART_MS);
  }

  function openSizeChart(layer, trigger) {
    if (!layer) return;
    layer._sizeChartClosing = false;
    layer._sizeChartToken = (layer._sizeChartToken || 0) + 1;
    const token = layer._sizeChartToken;
    pinProductInfo();
    if (layer.parentElement !== document.body) document.body.appendChild(layer);
    layer.hidden = false;
    layer.classList.add("is-open");
    layer._sizeChartReturn = trigger || layer._sizeChartReturn || null;
    // assets/size-finder.js sets up "Find your size" on this.
    layer.dispatchEvent(new CustomEvent("size-chart:open", { bubbles: true, detail: layer._sizeChartReturn }));
    if (window.Belnay && typeof window.Belnay.lockPageScroll === "function") {
      window.Belnay.lockPageScroll("size-chart");
    }
    const reveal = () => {
      if (layer._sizeChartToken !== token) return;
      layer.classList.add("is-visible");
    };
    if (prefersReduced) reveal();
    else requestAnimationFrame(() => requestAnimationFrame(reveal));
    const closeBtn = layer.querySelector(".size-chart__close");
    if (closeBtn && closeBtn.focus) closeBtn.focus({ preventScroll: true });
  }

  function bindSizeChart(scope) {
    (scope || document).querySelectorAll("[data-size-chart-open]").forEach((openBtn) => {
      if (openBtn.dataset.sizeChartBound === "1") return;
      const id = openBtn.getAttribute("aria-controls");
      const layer = id ? document.getElementById(id) : null;
      if (!layer) return;
      if (openBtn.closest("[data-quick-view-content]")) layer.dataset.sizeChartHost = "quick-view";
      openBtn.dataset.sizeChartBound = "1";
      openBtn.addEventListener("click", (event) => {
        event.preventDefault();
        openSizeChart(layer, openBtn);
      });
      layer.querySelectorAll("[data-size-chart-close]").forEach((btn) => {
        if (btn.dataset.sizeChartBound === "1") return;
        btn.dataset.sizeChartBound = "1";
        btn.addEventListener("click", () => closeSizeChart(layer));
      });
    });
  }

  window.Belnay.bindSizeChart = bindSizeChart;
  window.Belnay.closeSizeCharts = () => {
    document.querySelectorAll("[data-size-chart-layer]").forEach((layer) => closeSizeChart(layer));
  };

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Escape" && event.key !== "Tab") return;
      const open = document.querySelector("[data-size-chart-layer]:not([hidden])");
      if (!open) return;
      if (event.key === "Tab") {
        // Keep Tab inside the dialog: the page behind it is not in reach.
        const stops = Array.from(open.querySelectorAll("button, input, [tabindex]")).filter(
          (node) => node.tabIndex >= 0 && node.getClientRects().length
        );
        const edge = stops[event.shiftKey ? 0 : stops.length - 1];
        if (!stops.length || (document.activeElement !== edge && open.contains(document.activeElement))) return;
        event.preventDefault();
        stops[event.shiftKey ? stops.length - 1 : 0].focus();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      closeSizeChart(open);
    },
    true
  );


  document.addEventListener("dragstart", (event) => {
    if (event.target && event.target.tagName === "IMG") event.preventDefault();
  });

  const header = document.querySelector("[data-header]");
  const logoLink = document.querySelector(".header__logo");
  if (logoLink) {
    logoLink.addEventListener("click", (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      let dest;
      try {
        dest = new URL(logoLink.getAttribute("href") || "/", window.location.origin);
      } catch (err) {
        return;
      }
      const here = window.location.pathname.replace(/\/$/, "") || "/";
      const there = dest.pathname.replace(/\/$/, "") || "/";
      if (here !== there || dest.search !== window.location.search) return;
      /* Already on this page. A reload paints the same spot again.
         The wordmark returns to the start. */
      event.preventDefault();
      if (window.location.hash) {
        history.replaceState(history.state, "", dest.pathname + dest.search);
      }
      try {
        sessionStorage.setItem("belnay-scroll-y:" + location.pathname + location.search, "0");
        sessionStorage.removeItem("belnay-scroll-force");
      } catch (err2) {}
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, left: 0, behavior: reduce ? "auto" : "smooth" });
    });
  }
  if (header) {
    /* Colour only. Position is CSS: sticky on the phone
       (snippets/phone-header-lock.liquid), fixed on the desktop homepage. */
    const onScroll = () => {
      if (overlayScrollLocked()) return;
      if (phoneRestoreY() != null) {
        settlePhoneScroll();
        return;
      }
      header.classList.toggle("is-scrolled", headerPastPin(window.scrollY || 0));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.Belnay.headerPinBound = true;
    window.addEventListener("resize", () => {
      pinLine = 0;
    }, { passive: true });
  }

  /* Shop's sign-in sheet sits in the header; the body class turns the
     homepage header white while it is open (header.css). */
  function bindAccountSheet() {
    const account = document.querySelector("shopify-account");
    if (!account || account.dataset.sheetBound === "1") return;
    account.dataset.sheetBound = "1";
    account.addEventListener("open", () => document.body.classList.add("account-sheet-open"));
    account.addEventListener("close", () => document.body.classList.remove("account-sheet-open"));
  }
  bindAccountSheet();
  window.addEventListener("load", bindAccountSheet);

  const navToggle = document.querySelector("[data-nav-toggle]");
  const mobileNav = document.querySelector("[data-mobile-nav]");
  /* Keep the drawer on the body. A fixed panel inside the sticky header
     is what lets Safari clip the bar while the page is moving. */
  if (mobileNav && mobileNav.parentElement !== document.body) {
    document.body.appendChild(mobileNav);
  }

  let navCloseToken = 0;
  let navCloseTimer = 0;
  let navClosePanel = null;
  let navCloseOnEnd = null;

  function clearNavCloseWait() {
    if (navCloseTimer) window.clearTimeout(navCloseTimer);
    navCloseTimer = 0;
    if (navClosePanel && navCloseOnEnd) navClosePanel.removeEventListener("transitionend", navCloseOnEnd);
    navClosePanel = null;
    navCloseOnEnd = null;
  }

  function cssTimeMs(value) {
    const raw = String(value || "0s").split(",")[0].trim();
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return 0;
    return raw.endsWith("ms") ? n : n * 1000;
  }

  function finishMobileNavClose(token) {
    if (token !== navCloseToken || !mobileNav) return;
    if (!mobileNav.classList.contains("is-closing")) return;
    clearNavCloseWait();
    mobileNav.classList.remove("is-closing");
    mobileNav.classList.remove("is-shop");
    mobileNav.style.removeProperty("--nav-item-count");
    document.body.classList.remove("nav-open");
    unlockPageScroll("nav");
  }

  function setMobileNav(open) {
    if (!navToggle || !mobileNav) return;
    if (open) {
      navCloseToken += 1;
      clearNavCloseWait();
      mobileNav.style.removeProperty("--nav-item-count");
      mobileNav.classList.remove("is-closing");
      mobileNav.classList.add("is-open");
      navToggle.setAttribute("aria-expanded", "true");
      document.body.classList.add("nav-open");
      lockPageScroll("nav");
      if (window.BelnaySearch && typeof window.BelnaySearch.close === "function") {
        window.BelnaySearch.close();
      }
      closeShopMega();
      return;
    }
    if (mobileNav.classList.contains("is-closing")) return;
    if (!mobileNav.classList.contains("is-open")) return;
    const token = (navCloseToken += 1);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const scope = mobileNav.classList.contains("is-shop")
      ? mobileNav.querySelector(".mobile-nav__shop-view")
      : mobileNav.querySelector(".mobile-nav__root");
    const count = scope ? scope.querySelectorAll(".mobile-nav__item").length : 0;
    if (count) mobileNav.style.setProperty("--nav-item-count", String(count));
    mobileNav.classList.remove("is-open");
    mobileNav.classList.add("is-closing");
    navToggle.setAttribute("aria-expanded", "false");
    if (reduce) {
      finishMobileNavClose(token);
      return;
    }
    const panel = mobileNav.querySelector(".mobile-nav__panel");
    if (!panel) {
      finishMobileNavClose(token);
      return;
    }
    const motion = getComputedStyle(panel);
    const motionMs = cssTimeMs(motion.transitionDelay) + cssTimeMs(motion.transitionDuration);
    if (motionMs < 20) {
      finishMobileNavClose(token);
      return;
    }
    const started = performance.now();
    navCloseTimer = window.setTimeout(() => finishMobileNavClose(token), Math.round(motionMs + 120));
    navClosePanel = panel;
    navCloseOnEnd = (event) => {
      if (event.target !== panel) return;
      const name = event.propertyName;
      if (name !== "transform" && name !== "-webkit-transform") return;
      if (performance.now() - started < motionMs * 0.85) return;
      finishMobileNavClose(token);
    };
    panel.addEventListener("transitionend", navCloseOnEnd);
  }

  if (navToggle && mobileNav) {
    navToggle.addEventListener("click", () => {
      setMobileNav(!mobileNav.classList.contains("is-open"));
    });
    mobileNav.addEventListener("click", (event) => {
      if (event.target.closest("[data-nav-close]")) {
        setMobileNav(false);
        navToggle.focus();
        return;
      }
      if (event.target.closest("[data-menu-shop]")) {
        mobileNav.classList.add("is-shop");
        return;
      }
      if (event.target.closest("[data-menu-back]")) {
        mobileNav.classList.remove("is-shop");
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && mobileNav.classList.contains("is-open")) {
        setMobileNav(false);
        navToggle.focus();
      }
    });
  }

  const shopToggle = document.querySelector("[data-shop-toggle]");
  const shopMega = document.querySelector("[data-shop-mega]");
  const shopReveal = shopMega ? shopMega.querySelector("[data-shop-reveal]") : null;
  const desktopMenu = window.matchMedia("(min-width: 900px)");
  let shopOpenTimer = null;
  let shopCloseTimer = null;
  let shopChromeTimer = null;
  let shopOpenedBy = null;
  let shopCloseToken = 0;
  let shopCloseListener = null;
  let shopIgnoreBridgeUntil = 0;

  function clearShopTimer(name) {
    const timer = name === "open" ? shopOpenTimer : name === "close" ? shopCloseTimer : shopChromeTimer;
    if (timer) window.clearTimeout(timer);
    if (name === "open") shopOpenTimer = null;
    else if (name === "close") shopCloseTimer = null;
    else shopChromeTimer = null;
  }

  function clearShopCloseListener() {
    if (shopCloseListener && shopReveal) shopReveal.removeEventListener("transitionend", shopCloseListener);
    shopCloseListener = null;
  }

  function isShopZone(node) {
    if (!node || !shopToggle || !shopMega) return false;
    return shopToggle.contains(node) || shopMega.contains(node);
  }

  function eventOnShopButton(event) {
    const rect = shopToggle.getBoundingClientRect();
    return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
  }

  function openShopMega(openedBy = "click") {
    if (!shopToggle || !shopMega || !shopReveal) return;
    clearShopTimer("open");
    clearShopTimer("close");
    clearShopTimer("chrome");
    clearShopCloseListener();
    shopCloseToken += 1;
    const wasOpen = shopMega.classList.contains("is-open");
    const wasVisible = wasOpen || shopMega.classList.contains("is-closing");
    shopOpenedBy = openedBy;
    shopMega.hidden = false;
    if (!wasVisible) {
      shopMega.classList.add("is-closing");
      shopReveal.getBoundingClientRect();
    }
    shopMega.classList.remove("is-closing");
    shopMega.classList.add("is-open");
    shopMega.setAttribute("aria-hidden", "false");
    shopToggle.setAttribute("aria-expanded", "true");
    document.body.classList.add("shop-mega-open");
    if (!wasOpen) document.dispatchEvent(new CustomEvent("belnay:shop-mega-open"));
    if (mobileNav) {
      navCloseToken += 1;
      clearNavCloseWait();
      mobileNav.classList.remove("is-open", "is-closing", "is-shop");
      mobileNav.style.removeProperty("--nav-item-count");
      if (navToggle) navToggle.setAttribute("aria-expanded", "false");
      document.body.classList.remove("nav-open");
      unlockPageScroll("nav");
    }
  }

  function finishShopClose(token) {
    if (token !== shopCloseToken) return;
    clearShopCloseListener();
    if (shopMega.classList.contains("is-open")) return;
    shopMega.classList.remove("is-closing");
    document.body.classList.remove("shop-mega-open");
    shopIgnoreBridgeUntil = performance.now() + 120;
  }

  function closeShopMega(immediateChrome = false) {
    if (!shopToggle || !shopMega || !shopReveal) return;
    clearShopTimer("open");
    clearShopTimer("close");
    clearShopTimer("chrome");
    clearShopCloseListener();
    shopOpenedBy = null;
    shopCloseToken += 1;
    const token = shopCloseToken;
    const instant = immediateChrome || prefersReduced;
    if (instant) shopMega.classList.add("is-instant");
    shopMega.classList.remove("is-open");
    shopMega.hidden = false;
    shopMega.setAttribute("aria-hidden", "true");
    shopToggle.setAttribute("aria-expanded", "false");
    if (instant) {
      shopMega.classList.remove("is-closing");
      document.body.classList.remove("shop-mega-open");
      shopReveal.getBoundingClientRect();
      shopMega.classList.remove("is-instant");
      return;
    }
    shopMega.classList.add("is-closing");
    shopCloseListener = (event) => {
      if (event.propertyName !== "clip-path") return;
      finishShopClose(token);
    };
    shopReveal.addEventListener("transitionend", shopCloseListener);
    shopChromeTimer = window.setTimeout(() => {
      shopChromeTimer = null;
      finishShopClose(token);
    }, 500);
  }

  function scheduleShopClose() {
    if (shopOpenedBy === "click") return;
    clearShopTimer("close");
    shopCloseTimer = window.setTimeout(() => {
      shopCloseTimer = null;
      closeShopMega();
    }, 50);
  }

  function handleShopLeave(event) {
    if (!desktopMenu.matches || event.pointerType !== "mouse") return;
    if (isShopZone(event.relatedTarget)) return;
    clearShopTimer("open");
    scheduleShopClose();
  }

  if (shopToggle && shopMega) {
    shopToggle.addEventListener("click", (event) => {
      event.preventDefault();
      const isOpen = shopToggle.getAttribute("aria-expanded") === "true";
      if (isOpen && shopOpenedBy === "hover") {
        openShopMega("click");
      } else if (isOpen) {
        closeShopMega();
      } else {
        openShopMega("click");
      }
    });

    shopToggle.addEventListener("pointerenter", (event) => {
      if (!desktopMenu.matches || event.pointerType !== "mouse") return;
      clearShopTimer("close");
      if (shopMega.classList.contains("is-open")) return;
      const bridgeOnly = !eventOnShopButton(event);
      const closing = shopMega.classList.contains("is-closing") || performance.now() < shopIgnoreBridgeUntil;
      if (bridgeOnly && closing) return;
      openShopMega("hover");
    });
    shopToggle.addEventListener("pointerleave", handleShopLeave);
    shopMega.addEventListener("pointerenter", (event) => {
      if (!desktopMenu.matches || event.pointerType !== "mouse") return;
      clearShopTimer("open");
      clearShopTimer("close");
      if (!shopMega.classList.contains("is-open")) openShopMega("hover");
    });
    shopMega.addEventListener("pointerleave", handleShopLeave);
    shopMega.addEventListener("focusin", () => {
      clearShopTimer("close");
      if (shopMega.classList.contains("is-open")) shopOpenedBy = "click";
    });
    shopMega.addEventListener("focusout", () => {
      window.requestAnimationFrame(() => {
        if (!shopMega.contains(document.activeElement) && document.activeElement !== shopToggle) {
          closeShopMega();
        }
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && shopMega.classList.contains("is-open")) {
        closeShopMega(true);
        shopToggle.focus();
      }
    });

    document.addEventListener("click", (event) => {
      if (!shopMega.classList.contains("is-open")) return;
      if (shopMega.contains(event.target) || shopToggle.contains(event.target)) return;
      const overlayTrigger = event.target.closest(
        "[data-search-open], [data-cart-drawer-open], [data-nav-toggle], [data-currency-toggle]"
      );
      closeShopMega(Boolean(overlayTrigger));
    });
    document.addEventListener("belnay:currency-open", () => closeShopMega(true));
    desktopMenu.addEventListener("change", () => {
      if (!desktopMenu.matches) closeShopMega(true);
    });
  }

  bindAccordions(document);
  bindSizeChart(document);
})();
